import { useState, useEffect, useCallback } from 'react';

export interface UseBookmarkToggleParams {
  itemId: string;
  isFavorite: boolean;
  onToggle: (itemId: string, isFavorite: boolean) => Promise<void>;
}

export interface UseBookmarkToggleResult {
  isBookmarked: boolean;
  handleToggle: (e?: React.MouseEvent) => void;
}

/**
 * Custom hook for managing bookmark/favorite toggle behavior with optimistic updates.
 *
 * Features:
 * - Optimistic UI updates for immediate feedback
 * - Automatic rollback on error
 * - Event propagation handling (stopPropagation)
 * - Syncs with external isFavorite prop changes
 *
 * @example
 * ```tsx
 * const { isBookmarked, handleToggle } = useBookmarkToggle({
 *   itemId: item.opportunityId,
 *   isFavorite: item.isFavorite ?? false,
 *   onToggle: handleToggleFavorite,
 * });
 *
 * <button onClick={handleToggle}>
 *   <BookmarkIcon className={isBookmarked ? 'fill-current' : ''} />
 * </button>
 * ```
 */
export function useBookmarkToggle({
  itemId,
  isFavorite,
  onToggle,
}: UseBookmarkToggleParams): UseBookmarkToggleResult {
  const [isBookmarked, setIsBookmarked] = useState(isFavorite);

  // Sync internal state when external prop changes
  useEffect(() => {
    setIsBookmarked(isFavorite);
  }, [isFavorite]);

  const handleToggle = useCallback(
    (e?: React.MouseEvent) => {
      // Prevent event bubbling to parent elements (e.g., card click)
      e?.stopPropagation();

      // Optimistic update
      const nextState = !isBookmarked;
      setIsBookmarked(nextState);

      // Call the toggle handler and rollback on error
      onToggle(itemId, nextState).catch(() => {
        // Rollback to previous state on error
        setIsBookmarked(!nextState);
      });
    },
    [isBookmarked, itemId, onToggle],
  );

  return {
    isBookmarked,
    handleToggle,
  };
}
